"use client";

import { useEffect, useRef, useState } from "react";

type Props = {
  map: import("leaflet").Map | null;
  vehicleType: string;
  vehicleModel: string;
  vehicleNumber: string;
};

type RideNowRequest = {
  id: number;
  requestMode?: string | null;
  status: string;
  negotiationStatus?: string | null;
  rideNowExpiresAt?: string | null;
  fromLocation: string;
  fromLatitude?: number | null;
  fromLongitude?: number | null;
  toLocation: string;
  toLatitude?: number | null;
  toLongitude?: number | null;
  seats: number;
  bookingType?: "Seat" | "Parcel";
  parcelImage?: string | null;
  parcelDescription?: string | null;
  parcelWeightKg?: number | null;
  receiverPhone?: string | null;
  preferredVehicle?: string | null;
  passenger: { name: string };
  driverArrivedAt?: string | null;
  otpVerified?: boolean;
  match?: { pickupDistanceKm?: number | null };
};

export default function HomepageDriverLiveRadar({
  map,
  vehicleType,
  vehicleModel,
  vehicleNumber,
}: Props) {
  const [requests, setRequests] = useState<RideNowRequest[]>([]);
  const [activeTrip, setActiveTrip] = useState<RideNowRequest | null>(null);
  const [arrivalBusy, setArrivalBusy] = useState(false);
  const [driverCancelBusy, setDriverCancelBusy] = useState(false);

  async function cancelBeforeFareAcceptance() {
    const trip = activeTrip;
    if (
      !trip ||
      trip.status !== "Accepted" ||
      trip.negotiationStatus === "ACCEPTED" ||
      driverCancelBusy
    ) return;

    if (!window.confirm("Passenger ne abhi fare accept nahi kiya. Kya aap booking cancel karna chahte hain?")) return;

    setDriverCancelBusy(true);
    setTripActionError("");

    try {
      const response = await fetch(`/api/ride-requests/${trip.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "driver-cancel-before-fare" }),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          typeof result.message === "string"
            ? result.message
            : "Could not cancel booking."
        );
      }

      setActiveTrip(current => current?.id === trip.id ? null : current);
      setMessage("Booking cancelled. Waiting for new Ride Now requests.");
    } catch (cause) {
      setTripActionError(
        cause instanceof Error ? cause.message : "Could not cancel booking."
      );
    } finally {
      setDriverCancelBusy(false);
    }
  }


  const [passengerOtp, setPassengerOtp] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [startBusy, setStartBusy] = useState(false);

  const [tripActionError, setTripActionError] = useState("");

  const [driverPoint, setDriverPoint] = useState<[number, number] | null>(null);
  const activeRouteLayerRef = useRef<import("leaflet").LayerGroup | null>(null);
  const [routeMessage, setRouteMessage] = useState("");


  const markerLayerRef = useRef<import("leaflet").LayerGroup | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [fareText, setFareText] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState("");

  const selectedRequest = requests.find((request) => request.id === selectedId);

  const [message, setMessage] = useState("Checking nearby Ride Now requests...");

  useEffect(() => {
    let active = true;

    async function refresh() {
      try {
        const availability = await fetch("/api/driver-availability", {
          cache: "no-store",
        });

        if (!availability.ok) throw new Error("Driver availability unavailable.");

        const driver = await availability.json();

        if (driver.driverAvailableNow !== true) {
          if (active) {
            setRequests([]);
            setMessage("Go Online to receive Ride Now requests.");
          }
          return;
        }

        if (driver.driverAvailableNow !== true) {
          if (active) setDriverPoint(null);
        }

        const lat = Number(driver.driverLiveLatitude);
        const lng = Number(driver.driverLiveLongitude);

        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          if (active) setMessage("Waiting for fresh DRIVER GPS...");
          return;
        }

        if (active && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180 &&
            !(lat === 0 && lng === 0)) {
          setDriverPoint((previous) =>
            previous && previous[0] === lat && previous[1] === lng
              ? previous
              : [lat, lng]
          );
        }

        const params = new URLSearchParams({
          driverLat: String(lat),
          driverLng: String(lng),
          radiusKm: "25",
          vehicleType,
        });

        const response = await fetch(`/api/ride-requests?${params}`, {
          cache: "no-store",
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.message || `Ride Now API HTTP ${response.status}`);
        }

        const data: unknown = await response.json();
        if (!active) return;

        const now = Date.now();
        const incoming = (Array.isArray(data) ? data : []).filter(
          (request: RideNowRequest) =>
            request.requestMode === "Now" &&
            request.status === "Open" &&
            (!request.rideNowExpiresAt ||
              new Date(request.rideNowExpiresAt).getTime() > now)
        );

        setRequests(incoming);
        setMessage(
          incoming.length
            ? `${incoming.length} active Ride Now request(s) nearby`
            : "Live Radar active — waiting for Passenger requests."
        );
      } catch (error) {
        if (active) {
          setMessage(
            error instanceof Error ? error.message : "Live Radar unavailable."
          );
        }
      }
    }

    async function refreshActiveTrip() {
      try {
        const response = await fetch(
          "/api/ride-requests?mode=ride-now-accepted",
          { cache: "no-store" }
        );

        if (!response.ok) return;

        const data: unknown = await response.json();
        if (!active) return;

        const trips = Array.isArray(data) ? data as RideNowRequest[] : [];
        setActiveTrip(trips[0] ?? null);
      } catch {
        // Keep the last known trip visible during temporary network errors.
      }
    }

    void refreshActiveTrip();
    void refresh();
    const timer = window.setInterval(() => void refresh(), 5000);
    const tripTimer = window.setInterval(
      () => void refreshActiveTrip(),
      5000
    );

    return () => {
      active = false;
      window.clearInterval(timer);
      window.clearInterval(tripTimer);
    };
  }, [vehicleType]);






  async function startActiveTrip() {
    if (!activeTrip || startBusy || activeTrip.status !== "Accepted" ||
        !activeTrip.otpVerified) return;

    const tripId = activeTrip.id;
    setStartBusy(true);
    setTripActionError("");

    try {
      const response = await fetch(`/api/ride-requests/${tripId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "start" }),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.message || "Unable to start trip.");
      }

      const current = await fetch(
        "/api/ride-requests?mode=ride-now-accepted",
        { cache: "no-store" }
      );

      if (!current.ok) {
        throw new Error("Trip started. Refresh to see the updated status.");
      }

      const trips: unknown = await current.json();
      if (Array.isArray(trips)) {
        setActiveTrip(
          trips.find((trip: RideNowRequest) => trip.id === tripId) ?? null
        );
      }

      window.alert("🚗 Trip started. Follow the destination route.");
    } catch (error) {
      setTripActionError(
        error instanceof Error ? error.message : "Unable to start trip."
      );
    } finally {
      setStartBusy(false);
    }
  }

  async function verifyPassengerOtp() {
    if (!activeTrip || otpBusy || !activeTrip.driverArrivedAt ||
        activeTrip.otpVerified || activeTrip.status !== "Accepted") return;

    const otp = passengerOtp.trim();
    if (!/^\d{4}$/.test(otp)) {
      setTripActionError("Enter the Passenger's 4-digit OTP.");
      return;
    }

    setOtpBusy(true);
    setTripActionError("");

    try {
      const response = await fetch(`/api/ride-requests/${activeTrip.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "verify-otp", otp }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || "OTP verification failed.");
      }

      const current = await fetch(
        "/api/ride-requests?mode=ride-now-accepted",
        { cache: "no-store" }
      );

      if (!current.ok) {
        throw new Error("OTP verified. Refresh trip status.");
      }

      const trips: unknown = await current.json();
      if (Array.isArray(trips)) {
        setActiveTrip(
          trips.find((trip: RideNowRequest) => trip.id === activeTrip.id) ?? null
        );
      }

      setPassengerOtp("");
      window.alert("✅ Passenger OTP verified.");
    } catch (error) {
      setTripActionError(
        error instanceof Error ? error.message : "OTP verification failed."
      );
    } finally {
      setOtpBusy(false);
    }
  }

  async function confirmDriverArrival() {
    if (!activeTrip || arrivalBusy || activeTrip.status !== "Accepted" ||
        activeTrip.driverArrivedAt) return;

    setArrivalBusy(true);
    setTripActionError("");

    try {
      const response = await fetch(`/api/ride-requests/${activeTrip.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "driver-arrived" }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || "Unable to confirm pickup arrival.");
      }

      const current = await fetch(
        "/api/ride-requests?mode=ride-now-accepted",
        { cache: "no-store" }
      );

      if (!current.ok) {
        throw new Error("Arrival saved. Unable to refresh trip status.");
      }

      const trips: unknown = await current.json();
      setActiveTrip(
        Array.isArray(trips)
          ? (trips.find((trip: RideNowRequest) => trip.id === activeTrip.id) ?? null)
          : null
      );

      window.alert("📍 Arrival confirmed. Ask Passenger for pickup OTP.");
    } catch (error) {
      setTripActionError(
        error instanceof Error ? error.message : "Arrival confirmation failed."
      );
    } finally {
      setArrivalBusy(false);
    }
  }

  async function acceptSelectedRide() {
    if (!selectedRequest || accepting) return;

    const price = Number(fareText);
    if (!fareText.trim() || !Number.isFinite(price) || price < 0) {
      setAcceptError("Please enter a valid fare before accepting.");
      return;
    }

    setAccepting(true);
    setAcceptError("");

    try {
      const availabilityResponse = await fetch("/api/driver-availability", {
        cache: "no-store",
      });

      if (!availabilityResponse.ok) {
        throw new Error("Unable to verify DRIVER live location.");
      }

      const driver = await availabilityResponse.json();

      if (driver.driverAvailableNow !== true) {
        throw new Error("Please Go Online before accepting a Ride Now request.");
      }

      const lat = driver.driverLiveLatitude;
      const lng = driver.driverLiveLongitude;

      if (
        lat == null ||
        lng == null ||
        !Number.isFinite(Number(lat)) ||
        !Number.isFinite(Number(lng))
      ) {
        throw new Error("Waiting for valid DRIVER GPS location.");
      }

      const response = await fetch(
        `/api/ride-requests/${selectedRequest.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "accept",
            driverVehicleType: vehicleType,
            acceptedVehicleType: vehicleType,
            acceptedVehicleName: vehicleModel.trim(),
            acceptedVehicleNumber: vehicleNumber.trim().toUpperCase(),
            matchedRideId: null,
            maxPickupDistanceKm: 25,
            acceptedPrice: price,
            driverLatitude: Number(lat),
            driverLongitude: Number(lng),
          }),
        }
      );

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result.message || "Unable to accept Ride Now request.");
      }

      setRequests((current) =>
        current.filter((request) => request.id !== selectedRequest.id)
      );
      setSelectedId(null);
      setFareText("");
      window.alert("✅ Ride Now accepted. Drive to passenger pickup.");
    } catch (error) {
      setAcceptError(
        error instanceof Error ? error.message : "Unable to accept request."
      );
    } finally {
      setAccepting(false);
    }
  }


  // Draw active ride on the existing Homepage map; never create another map.
  useEffect(() => {
    if (!map || !activeTrip) return;

    const valid = (lat: number | null | undefined, lng: number | null | undefined) =>
      lat != null && lng != null &&
      Number.isFinite(Number(lat)) && Number.isFinite(Number(lng)) &&
      Number(lat) >= -90 && Number(lat) <= 90 &&
      Number(lng) >= -180 && Number(lng) <= 180 &&
      !(Number(lat) === 0 && Number(lng) === 0);

    const goingToDestination = activeTrip.status === "Started";
    const targetLat = goingToDestination ? activeTrip.toLatitude : activeTrip.fromLatitude;
    const targetLng = goingToDestination ? activeTrip.toLongitude : activeTrip.fromLongitude;

    if (!valid(targetLat, targetLng)) {
      setRouteMessage("Pickup/destination GPS unavailable.");
      return;
    }

    let cancelled = false;
    let layer: import("leaflet").LayerGroup | null = null;

    async function showRoute() {
      const module = await import("leaflet");
      if (cancelled || !map) return;
      const L = module.default ?? module;

      layer = L.layerGroup().addTo(map);
      activeRouteLayerRef.current = layer;

      const target: [number, number] = [Number(targetLat), Number(targetLng)];
      L.circleMarker(target, {
        radius: 12, color: "#fff", weight: 3,
        fillColor: goingToDestination ? "#059669" : "#e11d48",
        fillOpacity: 1,
      }).addTo(layer).bindTooltip(goingToDestination ? "🏁 Destination" : "📍 Passenger Pickup");

      if (!driverPoint || !valid(driverPoint[0], driverPoint[1])) {
        setRouteMessage("Waiting for DRIVER live GPS to calculate route.");
        return;
      }

      L.circleMarker(driverPoint, {
        radius: 11, color: "#fff", weight: 3,
        fillColor: "#2563eb", fillOpacity: 1,
      }).addTo(layer).bindTooltip("🚗 DRIVER live location");

      // When DRIVER and pickup overlap, show one clearly identifiable marker.
      const samePickup =
        !goingToDestination &&
        Math.abs(driverPoint[0] - target[0]) < 0.00005 &&
        Math.abs(driverPoint[1] - target[1]) < 0.00005;

      if (samePickup) {
        L.circleMarker(target, {
          radius: 23,
          color: "#ffffff",
          weight: 5,
          fillColor: "#7c3aed",
          fillOpacity: 0.95,
        })
          .addTo(layer)
          .bindTooltip("🚗 DRIVER is at Passenger pickup", {
            permanent: true,
            direction: "top",
            offset: [0, -20],
          });

        const focusMap = map as import("leaflet").Map & {
          _apnasaathiTripFocusKey?: string;
        };
        const focusKey = `${activeTrip?.id}:same-pickup`;
        if (focusMap._apnasaathiTripFocusKey !== focusKey) {
          focusMap._apnasaathiTripFocusKey = focusKey;
          map.setView(target, 17, { animate: true });
        }

        setRouteMessage("📍 DRIVER and Passenger pickup are at the same location.");
        return;
      }

      setRouteMessage("Calculating driving route...");

      try {
        const coordinates =
          `${driverPoint[1]},${driverPoint[0]};${target[1]},${target[0]}`;
        const response = await fetch(
          `https://router.project-osrm.org/route/v1/driving/${coordinates}?overview=full&geometries=geojson&steps=false`,
          { cache: "no-store" }
        );
        if (!response.ok) throw new Error("Routing service unavailable.");

        const data = await response.json();
        const geometry = data?.routes?.[0]?.geometry?.coordinates;

        if (data?.code !== "Ok" || !Array.isArray(geometry))
          throw new Error("No driving route found.");

        const points: [number, number][] = geometry
          .filter((point: unknown): point is number[] =>
            Array.isArray(point) && point.length >= 2 &&
            Number.isFinite(Number(point[0])) &&
            Number.isFinite(Number(point[1]))
          )
          .map((point: number[]) => [Number(point[1]), Number(point[0])]);

        if (cancelled || !layer || points.length < 2) return;

        L.polyline(points, {
          color: goingToDestination ? "#059669" : "#4f46e5",
          weight: 6, opacity: 0.95,
        }).addTo(layer);

        // Focus the existing Homepage map on the actual driving route.
        // Only adjust the camera when the trip or route target changes,
        // so live GPS updates do not repeatedly interrupt map gestures.
        const focusKey = `${activeTrip?.id}:${goingToDestination ? "destination" : "pickup"}`;
        const mapWithFocus = map as import("leaflet").Map & {
          _apnasaathiTripFocusKey?: string;
        };
        if (mapWithFocus._apnasaathiTripFocusKey !== focusKey) {
          mapWithFocus._apnasaathiTripFocusKey = focusKey;
          map.fitBounds(L.latLngBounds(points), {
            padding: [60, 60],
            maxZoom: 16,
            animate: true,
          });
        }

        setRouteMessage(
          goingToDestination
            ? "🛣️ Live road route to destination"
            : "🛣️ Live road route to Passenger pickup"
        );
      } catch {
        if (!cancelled) setRouteMessage("Road route unavailable. Showing GPS markers only.");
      }
    }

    void showRoute();

    return () => {
      cancelled = true;
      if (layer) map.removeLayer(layer);
      if (activeRouteLayerRef.current === layer)
        activeRouteLayerRef.current = null;
    };
  }, [
    map, activeTrip?.id, activeTrip?.status,
    activeTrip?.fromLatitude, activeTrip?.fromLongitude,
    activeTrip?.toLatitude, activeTrip?.toLongitude,
    driverPoint,
  ]);

  // Reuse the Homepage map. Never initialize or destroy the shared map.
  useEffect(() => {
    if (!map || activeTrip) return;

    let cancelled = false;
    let layer: import("leaflet").LayerGroup | null = null;

    void import("leaflet").then((module) => {
      if (cancelled) return;

      const L = module.default ?? module;
      layer = L.layerGroup().addTo(map);
      markerLayerRef.current = layer;

      for (const request of requests) {
        const lat = Number(request.fromLatitude);
        const lng = Number(request.fromLongitude);

        if (
          request.fromLatitude == null ||
          request.fromLongitude == null ||
          !Number.isFinite(lat) ||
          !Number.isFinite(lng) ||
          lat < -90 || lat > 90 ||
          lng < -180 || lng > 180
        ) continue;

        const marker = L.circleMarker([lat, lng], {
          radius: 15,
          color: "#ffffff",
          weight: 4,
          fillColor: "#e11d48",
          fillOpacity: 0.95,
        }).addTo(layer);

        marker.bindTooltip("⚡ Ride Now #" + request.id);
        marker.on("click", () => setSelectedId(request.id));
      }
    });

    return () => {
      cancelled = true;
      if (layer) map.removeLayer(layer);
      if (markerLayerRef.current === layer) markerLayerRef.current = null;
    };
  }, [map, requests]);

  return (
    <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-3">

      {!activeTrip && <p className="text-sm font-semibold text-slate-700">{message}</p>}

      {activeTrip && (
        <div className="space-y-2 rounded-xl border-2 border-emerald-500 bg-emerald-50 p-4">
          <h4 className="text-base font-bold text-emerald-900">
            {activeTrip.bookingType === "Parcel" ? "📦 Active Parcel" : "🚗 Active Trip"} #{activeTrip.id}
          </h4>
          <p className="text-sm text-slate-800">
            <strong>{activeTrip.bookingType === "Parcel" ? "Sender" : "Passenger"}:</strong> {activeTrip.passenger.name}
          </p>
          <p className="text-sm text-slate-800">
            <strong>Pickup:</strong> {activeTrip.fromLocation}
          </p>
          <p className="text-sm text-slate-800">
            <strong>Destination:</strong> {activeTrip.toLocation}
          </p>
          {activeTrip.bookingType === "Parcel" && (
            <div className="space-y-2 rounded-lg border border-emerald-200 bg-white p-3">
              <p className="text-sm font-bold text-emerald-900">
                📦 Active Parcel Details
              </p>
              <p className="break-words text-sm text-slate-800">
                {activeTrip.parcelDescription || "No description"}
              </p>
              <p className="text-sm text-slate-800">
                Weight: {activeTrip.parcelWeightKg ?? "Not specified"} kg
              </p>
              {activeTrip.receiverPhone && (
                <p className="text-sm text-slate-800">
                  <strong>📞 Receiver Mobile:</strong>{" "}
                  <a
                    href={`tel:${activeTrip.receiverPhone}`}
                    className="font-semibold text-indigo-700 underline"
                  >
                    {activeTrip.receiverPhone}
                  </a>
                </p>
              )}
              {activeTrip.parcelImage && (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={activeTrip.parcelImage}
                  alt="Active parcel photo"
                  className="h-40 w-40 rounded-lg border object-cover"
                />
              )}
            </div>
          )}

          <p className="text-sm font-semibold text-emerald-800">
            Status: {activeTrip.status}
          </p>
          {tripActionError && (
            <p role="alert" className="text-sm font-semibold text-red-700">
              {tripActionError}
            </p>
          )}
          {activeTrip.status === "Accepted" &&
            !activeTrip.driverArrivedAt &&
            activeTrip.negotiationStatus !== "ACCEPTED" && (
              <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-center text-sm font-bold text-amber-800">
                ⏳ Waiting for Passenger to Accept Fare
              </p>
            )}
          {activeTrip.status === "Accepted" &&
            !activeTrip.driverArrivedAt &&
            activeTrip.negotiationStatus !== "ACCEPTED" && (
              <button
                type="button"
                onClick={() => void cancelBeforeFareAcceptance()}
                disabled={driverCancelBusy}
                className="w-full rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700 disabled:opacity-50"
              >
                {driverCancelBusy ? "Cancelling..." : "❌ Cancel Booking"}
              </button>
            )}
          {activeTrip.status === "Accepted" &&
            !activeTrip.driverArrivedAt &&
            activeTrip.negotiationStatus === "ACCEPTED" && (
            <button
              type="button"
              disabled={arrivalBusy}
              onClick={() => void confirmDriverArrival()}
              className="w-full rounded-xl bg-indigo-600 px-4 py-3 text-sm font-bold text-white disabled:opacity-50"
            >
              {arrivalBusy ? "Confirming Arrival..." : "📍 I Have Arrived"}
            </button>
          )}
          {activeTrip.status === "Accepted" && activeTrip.otpVerified && (
            <button
              type="button"
              disabled={startBusy}
              onClick={() => void startActiveTrip()}
              className="w-full rounded-xl bg-emerald-700 px-4 py-3 text-sm font-bold text-white disabled:opacity-50"
            >
              {startBusy ? "Starting Trip..." : "🚗 Start Trip"}
            </button>
          )}

          {activeTrip.status === "Accepted" &&
            activeTrip.driverArrivedAt &&
            !activeTrip.otpVerified && (
              <div className="space-y-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm font-bold text-amber-900">
                  🔐 Verify Passenger Pickup OTP
                </p>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={4}
                  value={passengerOtp}
                  onChange={(event) =>
                    setPassengerOtp(event.target.value.replace(/\D/g, "").slice(0, 4))
                  }
                  placeholder="4-digit OTP"
                  className="w-full rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  disabled={otpBusy || !/^\d{4}$/.test(passengerOtp)}
                  onClick={() => void verifyPassengerOtp()}
                  className="w-full rounded-xl bg-amber-600 px-4 py-3 text-sm font-bold text-white disabled:opacity-50"
                >
                  {otpBusy ? "Verifying..." : "🔐 Verify OTP"}
                </button>
              </div>
            )}
          <p role="status" className="text-xs font-semibold text-indigo-700">
            {routeMessage}
          </p>
          <p className="text-xs font-semibold text-slate-700">
            DRIVER GPS: {driverPoint
              ? `${driverPoint[0].toFixed(5)}, ${driverPoint[1].toFixed(5)}`
              : "Waiting for GPS"}
          </p>
          <p className="text-xs font-semibold text-slate-700">
            Pickup GPS: {activeTrip.fromLatitude != null && activeTrip.fromLongitude != null
              ? `${Number(activeTrip.fromLatitude).toFixed(5)}, ${Number(activeTrip.fromLongitude).toFixed(5)}`
              : "Unavailable"}
          </p>
          {driverPoint && activeTrip.fromLatitude != null && activeTrip.fromLongitude != null && (
            <p className="text-xs font-bold text-indigo-800">
              Straight-line pickup distance: {(
                6371 * 2 * Math.asin(Math.min(1, Math.sqrt(
                  Math.sin((Number(activeTrip.fromLatitude) - driverPoint[0]) * Math.PI / 360) ** 2 +
                  Math.cos(driverPoint[0] * Math.PI / 180) *
                  Math.cos(Number(activeTrip.fromLatitude) * Math.PI / 180) *
                  Math.sin((Number(activeTrip.fromLongitude) - driverPoint[1]) * Math.PI / 360) ** 2
                )))
              ).toFixed(3)} km
            </p>
          )}
        </div>
      )}

      {!activeTrip && selectedRequest && (
        <div className="space-y-3 rounded-xl border-2 border-rose-500 bg-rose-50 p-4">
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-base font-bold text-rose-800">
              {selectedRequest.bookingType === "Parcel" ? "📦 Parcel Request" : "⚡ Ride Now"} #{selectedRequest.id}
            </h4>
            <button
              type="button"
              onClick={() => setSelectedId(null)}
              className="rounded-lg bg-white px-3 py-1 text-sm font-semibold text-slate-700"
            >
              Close
            </button>
          </div>

          <p className="text-sm text-slate-800">
            <strong>{selectedRequest.bookingType === "Parcel" ? "Sender" : "Passenger"}:</strong> {selectedRequest.passenger.name}
          </p>
          <p className="text-sm text-slate-800">
            <strong>Pickup:</strong> {selectedRequest.fromLocation}
          </p>
          <p className="text-sm text-slate-800">
            <strong>Drop:</strong> {selectedRequest.toLocation}
          </p>
          <p className="text-sm text-slate-700">
            {selectedRequest.bookingType === "Parcel"
              ? "📦 Parcel delivery"
              : `${selectedRequest.seats} seat(s)`}
            {selectedRequest.match?.pickupDistanceKm != null
              ? ` · ${selectedRequest.match.pickupDistanceKm.toFixed(1)} km pickup`
              : ""}
          </p>

          {selectedRequest.bookingType === "Parcel" && (
            <div className="space-y-2 rounded-lg border bg-white p-3">
              <p className="text-sm font-bold">📦 Parcel Details</p>
              <p className="break-words text-sm">
                {selectedRequest.parcelDescription || "No description"}
              </p>
              <p className="text-sm">
                Weight: {selectedRequest.parcelWeightKg ?? "Not specified"} kg
              </p>
              {selectedRequest.parcelImage && (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={selectedRequest.parcelImage}
                  alt="Parcel photo"
                  className="h-36 w-36 rounded-lg border object-cover"
                />
              )}
            </div>
          )}

          <label className="block space-y-1">
            <span className="text-sm font-semibold text-slate-800">
              Your fare (₹)
            </span>
            <input
              type="number"
              min="0"
              step="1"
              inputMode="decimal"
              value={fareText}
              onChange={(event) => setFareText(event.target.value)}
              placeholder="Enter fare amount"
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
            />
          </label>
          {acceptError && (
            <p role="alert" className="text-sm font-semibold text-red-700">
              {acceptError}
            </p>
          )}
          <button
            type="button"
            disabled={accepting}
            onClick={() => void acceptSelectedRide()}
            className="w-full rounded-xl bg-rose-600 px-4 py-3 text-sm font-bold text-white disabled:opacity-50"
          >
            {accepting ? "Accepting Ride..." : "✅ Accept Ride"}
          </button>
        </div>
      )}

      {!activeTrip && requests.map((request) => (
        <div key={request.id} className="space-y-1 rounded-xl border border-rose-200 bg-rose-50 p-3">
          <p className="text-sm font-bold text-rose-800">
            {request.bookingType === "Parcel" ? "📦 Parcel Request" : "⚡ Ride Now"} #{request.id}
          </p>
          <p className="text-sm text-slate-800">{request.fromLocation} → {request.toLocation}</p>
          <p className="text-xs text-slate-600">
            {request.bookingType === "Parcel"
              ? `📦 Parcel · ${request.parcelWeightKg ?? "?"} kg`
              : `${request.seats} seat(s)`} · {request.preferredVehicle || "Any"}
            {request.match?.pickupDistanceKm != null
              ? ` · ${request.match.pickupDistanceKm.toFixed(1)} km away`
              : ""}
          </p>
        </div>
      ))}
    </section>
  );
}
